<template>
  <v-dialog v-model="dialog" width="680" @click:outside="cancel" :persistent="isLoading" data-testid="bulk-monthly-dialog">
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('tanaoroshiResultXlsxBulkDownloadDialog.label.title') }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <!-- エラー / 該当データなしのメッセージ（APIがerrorMessageで返却） -->
        <div v-if="messageLines.length > 0" class="ml-4" style="color: red" role="alert" data-testid="bulk-monthly-message">
          <div v-for="(line, idx) in messageLines" :key="idx">{{ line }}</div>
        </div>

        <v-container>
          <v-row>
            <v-col cols="12">
              <!-- YearMonthPickerは複数ルート要素のため、ラッパーにdata-testidを付与 -->
              <div data-testid="bulk-monthly-month">
                <YearMonthPicker v-model="selectedDate"
                  :label="$t('tanaoroshiResultXlsxBulkDownloadDialog.label.selectedDate')"></YearMonthPicker>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <!-- 対象店舗：複数選択（初期値は全選択） -->
              <v-select
                data-testid="bulk-monthly-sites"
                v-model="selectedCodes"
                :items="shopItems"
                item-title="code_name"
                item-value="code"
                :label="$t('tanaoroshiResultXlsxBulkDownloadDialog.label.selectedShop')"
                density="compact"
                hide-details="auto"
                multiple
                :loading="isShopLoading"
                :disabled="isShopLoading || isLoading"
              >
                <template v-slot:prepend-item>
                  <v-list-item data-testid="bulk-monthly-sites-all"
                    :title="$t('tanaoroshiResultXlsxBulkDownloadDialog.label.selectAll')" @click="toggleAllShops">
                    <template v-slot:prepend>
                      <v-checkbox-btn
                        :model-value="isAllSelected"
                        :indeterminate="isSomeSelected"
                        :ripple="false"
                      />
                    </template>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <!-- 複数選択時は1件目のみ表示し、残りは「他n件」にまとめる -->
                <template v-slot:selection="{ item, index }">
                  <div v-if="index === 0" class="bulk-sites-selection" data-testid="bulk-monthly-sites-summary">
                    <span v-if="isAllSelected && shopItems.length > 1">
                      {{ $t('tanaoroshiResultXlsxBulkDownloadDialog.label.shopAll', [shopItems.length]) }}
                    </span>
                    <template v-else>
                      <v-chip size="small" label class="bulk-sites-chip">{{ item.title }}</v-chip>
                      <span v-if="selectedCodes.length > 1" class="text-caption ml-2 text-no-wrap">
                        {{ $t('tanaoroshiResultXlsxBulkDownloadDialog.label.shopOthers', [selectedCodes.length - 1]) }}
                      </span>
                    </template>
                  </div>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-container>

      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="isLoading" data-testid="bulk-monthly-cancel">
          {{ $t('common.action.cancel') }}
        </v-btn>
        <v-btn color="indigo darken-1" dark @click="ok" :loading="isLoading" :disabled="!isFormValid"
          data-testid="bulk-monthly-ok">
          {{ $t('common.action.download') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/apis/staff";
import YearMonthPicker from "@/components/YearMonthPicker";

export default {
  components: { YearMonthPicker },
  props: {
    handleOk: {
      type: Function,
      required: true,
    },
    handleCancel: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      dialog: true,
      isLoading: false,       // ダウンロードAPI実行中
      isShopLoading: false,   // 店舗一覧取得中
      errorMessage: "",
      errorList: [],
      selectedDate: null,     // 対象年月（YYYY-MM）
      shopItems: [],          // 全店舗
      selectedCodes: [],      // 選択中の店舗コード
    };
  },

  computed: {
    // 全店舗が選択されているか
    isAllSelected() {
      return this.shopItems.length > 0 && this.selectedCodes.length == this.shopItems.length;
    },
    // 一部の店舗のみ選択されているか
    isSomeSelected() {
      return this.selectedCodes.length > 0 && !this.isAllSelected;
    },
    // 年月と店舗が選択済みか
    isFormValid() {
      return !!this.selectedDate && this.selectedCodes.length > 0;
    },
    // 表示するメッセージ（エラー or 該当データなし）
    messageLines() {
      const lines = [];
      if (this.errorMessage) lines.push(this.errorMessage);
      (this.errorList ?? []).forEach((e) => {
        const text = typeof e === "string" ? e : e?.message;
        if (text) lines.push(text);
      });
      return lines;
    },
  },

  watch: {
    // Escキー等でダイアログが閉じられた場合も親に通知する
    dialog(val) {
      if (!val) this.handleCancel();
    },
  },

  mounted() {
    this.loadShops();
  },

  methods: {
    clearMessages() {
      this.errorMessage = "";
      this.errorList = [];
    },
    // 全店舗の取得（selectSiteFlag：0 = 他店舗含む）
    async loadShops() {
      this.isShopLoading = true;
      try {
        const response = await api.post("/store/search/minimal", { selectSiteFlag: 0 });
        if (response.data?.status == 1) {
          this.errorMessage = response.data?.errorMessage || this.$t('common.message.unknownError');
          return;
        }
        this.shopItems = (response.data?.results?.shopList ?? []).map((e) => ({
          name: e.name,
          code: e.code,
          code_name: e.code + "_" + e.name,
        }));
        // 初期値は全選択
        this.selectedCodes = this.shopItems.map((e) => e.code);
      } catch (e) {
        this.errorMessage = this.$t('common.message.unknownError');
      } finally {
        this.isShopLoading = false;
      }
    },
    // 「すべて選択」の切り替え
    toggleAllShops() {
      this.selectedCodes = this.isAllSelected ? [] : this.shopItems.map((e) => e.code);
    },
    cancel() {
      if (this.isLoading) return;
      this.handleCancel();
    },
    async ok() {
      // 実行中の連打防止
      if (this.isLoading || !this.isFormValid) return;
      this.isLoading = true;
      this.clearMessages();
      try {
        // 全店舗選択時は空配列を送信（APIは「空 = 全店舗」）
        const selected = new Set(this.selectedCodes);
        const siteCodeList = this.isAllSelected
          ? []
          : this.shopItems.map((e) => e.code).filter((code) => selected.has(code));
        const targetYearMonth = this.selectedDate.replaceAll("-", "");
        const response = await api.postWithConfig("/tanaoroshi/download-xlsx-monthly-all-site", {
          targetYearMonth: targetYearMonth,
          siteCodeList: siteCodeList,
        }, { responseType: "blob" });
        const contentType = response.headers["content-type"] || response.data?.type || "";
        // 失敗・該当データなしの場合はJSONでエラー内容が返却される
        if (contentType.indexOf("application/json") >= 0) {
          const json = JSON.parse(await response.data.text());
          this.errorMessage = json?.errorMessage || this.$t('common.message.unknownError');
          this.errorList = json?.errorList ?? [];
          return;
        }
        this.downloadBlob(response, contentType, targetYearMonth);
        this.handleOk();
      } catch (e) {
        this.errorMessage = this.$t('common.message.unknownError');
      } finally {
        this.isLoading = false;
      }
    },
    // 棚卸実績ファイル（CSV）をまとめたZIP（ファイル名はContent-Dispositionから取得）
    downloadBlob(response, contentType, targetYearMonth) {
      const disposition = response.headers["content-disposition"] || "";
      let filename = "";
      // filename*=UTF-8''... 優先、無ければ filename="..."
      const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
      if (star) {
        filename = decodeURIComponent(star[1]);
      } else {
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        if (plain) filename = plain[1];
      }
      if (!filename) {
        const prefix = this.$t('tanaoroshiResultXlsxBulkDownloadDialog.fileNamePrefix.fallback');
        filename = `${prefix}${targetYearMonth}.zip`;
      }
      const blob = new Blob([response.data], { type: contentType || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style lang="scss" scoped>
/* 店舗の選択表示（1件目＋「他n件」）：枠内に収める */
.bulk-sites-selection {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}
.bulk-sites-chip.v-chip.v-chip--size-small {
  min-width: 0;
  margin: 0;
  height: 22px;
}
.bulk-sites-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
